import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useI18n } from "@/contexts/I18nContext";
import { Section } from "@/components/Primitives";
import { RpSelect } from "@/components/RpSelect";
import { StaggerRoot } from "@/components/motion";

function Field({ label, children }) {
  return (
    <div>
      <label className="text-xs text-gray-500 mb-1 block">{label}</label>
      {children}
    </div>
  );
}

export default function Settings() {
  const { t, lang, setLang } = useI18n();
  const [s, setS] = useState({
    restaurant_name: "", address: "", phone: "", email: "", tax_number: "",
    receipt_footer: "", receipt_width: "80mm",
    tax_enabled: false, tax_percent: 0,
    service_enabled: false, service_percent: 0,
    default_language: "dari",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/settings")
      .then((r) => r.data && setS((prev) => ({ ...prev, ...r.data })))
      .catch((e) => toast.error(apiError(e)));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings", s);
      toast.success(t("success"));
      setLang(s.default_language || "dari");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };



  return (
    <StaggerRoot className="space-y-6">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h1 className="text-2xl md:text-3xl font-bold text-gray-900 font-heading">{t("settings")}</h1>
        <button data-testid="save-settings" onClick={save} disabled={saving} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[#071A33] text-white text-sm font-medium hover:bg-[#7F1020] disabled:opacity-60">
          <Save className="w-4 h-4" /> {t("save")}
        </button>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <Section title={t("restaurantInfo")}>
          <div className="grid grid-cols-2 gap-3">
            <Field label={t("restaurantName")}>
              <input value={s.restaurant_name || ""} onChange={(e) => setS({ ...s, restaurant_name: e.target.value })} className="w-full h-11 px-3 rounded-xl border border-gray-200" />
            </Field>
            <Field label={t("phone")}>
              <input value={s.phone || ""} onChange={(e) => setS({ ...s, phone: e.target.value })} className="w-full h-11 px-3 rounded-xl border border-gray-200" />
            </Field>
            <Field label={t("address")}>
              <input value={s.address || ""} onChange={(e) => setS({ ...s, address: e.target.value })} className="w-full h-11 px-3 rounded-xl border border-gray-200" />
            </Field>
            <Field label="ایمیل">
              <input value={s.email || ""} onChange={(e) => setS({ ...s, email: e.target.value })} className="w-full h-11 px-3 rounded-xl border border-gray-200" />
            </Field>
            <Field label="شماره مالیات">
              <input value={s.tax_number || ""} onChange={(e) => setS({ ...s, tax_number: e.target.value })} className="w-full h-11 px-3 rounded-xl border border-gray-200" />
            </Field>
          </div>
        </Section>

        <Section title={t("receiptSettings")}>
          <div className="space-y-3">
            <Field label={t("receiptFooter")}>
              <input value={s.receipt_footer || ""} onChange={(e) => setS({ ...s, receipt_footer: e.target.value })} className="w-full h-11 px-3 rounded-xl border border-gray-200" />
            </Field>
            <Field label={t("receiptWidth")}>
              <RpSelect
                value={s.receipt_width || "80mm"}
                onValueChange={(v) => setS({ ...s, receipt_width: v })}
                options={[
                  { value: "58mm", label: "58mm" },
                  { value: "80mm", label: "80mm" },
                ]}
              />
            </Field>
          </div>
        </Section>

        <Section title={t("financialSettings")}>
          <div className="space-y-4">
            <Switch label={t("taxEnabled")} value={s.tax_enabled} onChange={(v) => setS({ ...s, tax_enabled: v })} />
            <Field label={t("taxPercent")}>
              <input type="number" value={s.tax_percent || 0} onChange={(e) => setS({ ...s, tax_percent: Number(e.target.value) })} className="w-full h-11 px-3 rounded-xl border border-gray-200 tabular" />
            </Field>
            <Switch label={t("serviceEnabled")} value={s.service_enabled} onChange={(v) => setS({ ...s, service_enabled: v })} />
            <Field label={t("servicePercent")}>
              <input type="number" value={s.service_percent || 0} onChange={(e) => setS({ ...s, service_percent: Number(e.target.value) })} className="w-full h-11 px-3 rounded-xl border border-gray-200 tabular" />
            </Field>
          </div>
        </Section>

        <Section title={t("languageSettings")}>
          <Field label={t("defaultLanguage")}>
            <RpSelect
              value={s.default_language || "dari"}
              onValueChange={(v) => setS({ ...s, default_language: v })}
              options={[
                { value: "dari", label: "دری" },
                { value: "pashto", label: "پښتو" },
              ]}
            />
          </Field>
        </Section>
      </div>
    </StaggerRoot>
  );
}

function Switch({ label, value, onChange }) {
  return (
    <button type="button" onClick={() => onChange(!value)} className="w-full flex items-center justify-between bg-gray-50 hover:bg-gray-100 rounded-xl p-3">
      <span className="text-sm text-gray-700">{label}</span>
      <span className={`w-10 h-6 rounded-full p-0.5 transition-colors ${value ? "bg-[#071A33]" : "bg-gray-300"}`}>
        <span className={`block w-5 h-5 rounded-full bg-white transition-transform ${value ? "translate-x-[-1rem]" : "translate-x-0"}`} />
      </span>
    </button>
  );
}
